import { useState } from "react";
import { Accordion, Badge, Group, Stack, TextInput, Tabs, Text, Box } from "@repo/ui/core";
import { IconSearch } from "@repo/ui/icons";
import type { FAQ } from "@data/types";
import classes from "@styles/onsite.module.css";

const CATEGORY_COLORS: Record<string, string> = {
  Architecture: "violet",
  Security: "red",
  Compliance: "yellow",
  Integration: "blue",
  "Developer Experience": "green",
  Infrastructure: "cyan",
};

const CATEGORY_CLASSES: Record<string, string | undefined> = {
  Architecture: classes.badgeArchitecture,
  Security: classes.badgeSecurity,
  Compliance: classes.badgeCompliance,
  Integration: classes.badgeIntegration,
  "Developer Experience": classes.badgeDeveloperExperience,
  Infrastructure: classes.badgeInfrastructure,
};

type Props = {
  faqs: FAQ[];
};

export function FAQSection({ faqs }: Props) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("__all__");

  const categories = [...new Set(faqs.map((f) => f.category))];

  const filtered = faqs.filter((faq) => {
    const matchesCategory = activeCategory === "__all__" || faq.category === activeCategory;
    const matchesSearch =
      !search ||
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase()) ||
      faq.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <Stack gap="lg">
      <TextInput
        placeholder="Search FAQs..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="md"
      />

      <Tabs
        value={activeCategory}
        onChange={(v) => setActiveCategory(v ?? "__all__")}
        variant="pills"
      >
        <Tabs.List>
          <Tabs.Tab value="__all__">All</Tabs.Tab>
          {categories.map((cat) => (
            <Tabs.Tab key={cat} value={cat}>
              {cat}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {filtered.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No matching questions found.
        </Text>
      ) : (
        <Accordion variant="separated" radius="md">
          {filtered.map((faq) => (
            <Accordion.Item key={faq.id} value={faq.id}>
              <Accordion.Control>
                <Group gap="sm" wrap="nowrap">
                  <Box style={{ flex: 1 }}>
                    <Text fw={500}>{faq.question}</Text>
                  </Box>
                  <Badge
                    variant="light"
                    size="sm"
                    color={CATEGORY_COLORS[faq.category] ?? "gray"}
                    className={CATEGORY_CLASSES[faq.category]}
                    style={{ flexShrink: 0 }}
                  >
                    {faq.category}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Text>
                <Group gap={4} mt="sm">
                  {faq.tags.map((tag) => (
                    <Badge key={tag} variant="dot" size="xs" color={CATEGORY_COLORS[faq.category] ?? "gray"}>
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Stack>
  );
}
